/**
 * SetupStatusStrip — P2's surface half: the ONE status surface, ambient on
 * every settings section. Every chip is a StatusPill fed ONLY by
 * useClientCapabilities (zero local derivation — the grep gate proves it).
 *
 * Behavior (P2 spine + partner consultations):
 *  - Chips follow the `[State] · [Object]` template; amber chips deep-link
 *    to the owning section so the reminder lands on the specific action.
 *  - The WordPress chip ALWAYS passes verified={capabilities.wordpress.verified}
 *    — key presence never renders a bare "Connected" (gate i).
 *  - Plan-excluded products are absent entirely (never a visible row with a
 *    missing pill, and absence is visually distinct from the stale modifier).
 *  - ONE emphasis voice: first-run (nothing connected, something not set up)
 *    promotes exactly one "Start here · <first step>" action in coral —
 *    visually distinct from amber — and demotes every amber; post-first-run
 *    exactly ONE next action (the first action_needed in priority order) is
 *    promoted the same way. No two "act now" languages compete.
 *  - Stale/failure rides the pills via staleSuffixFor (one vocabulary, no
 *    second phrasing); the initial unverified load renders a stable-height
 *    placeholder — no spinners, no wrong claims, no layout shift.
 */

import { Link } from 'react-router-dom';
import { useTenantStore } from '@/stores/useTenantStore';
import {
  useClientCapabilities,
  type CapabilityState,
  type ClientCapabilities,
} from '@/hooks/useClientCapabilities';
import { StatusPill, staleSuffixFor } from './StatusPill';
import { COPY, STATE_LABELS } from '@/lib/copyTokens';
import { cn } from '@/lib/utils';
import type { ProductPackage } from '@/types/auth';

export type StripProduct = 'brandBible' | 'wordpress' | 'googleAds' | 'googleDocs';

export interface StripChip {
  product: StripProduct;
  /** The object half of the chip template, e.g. "Google Ads". */
  object: string;
  state: CapabilityState;
  /** Passed to StatusPill for products with a verification claim (WP). */
  verified?: boolean;
  /** Owning section for amber deep-links and the promoted action. */
  href: string;
  promoted: boolean;
  demoted: boolean;
}

export interface StripModel {
  chips: StripChip[];
  firstRun: boolean;
}

/** Priority order: Brand bible first (the generation prerequisite). */
const PRODUCT_ORDER: readonly StripProduct[] = [
  'brandBible',
  'wordpress',
  'googleAds',
  'googleDocs',
];

const SECTION_HREF: Record<StripProduct, string> = {
  brandBible: '/brand-bible',
  wordpress: COPY.sections.integrations.path,
  googleAds: COPY.sections.integrations.path,
  googleDocs: COPY.sections.integrations.path,
};

/** Plan-excluded products are absent as WHOLE chips — never shown, never locked. */
function includedProducts(pkg: ProductPackage): StripProduct[] {
  if (pkg === 'seo') return PRODUCT_ORDER.filter((p) => p !== 'googleAds');
  if (pkg === 'ads') return PRODUCT_ORDER.filter((p) => p === 'googleAds' || p === 'wordpress');
  return [...PRODUCT_ORDER];
}

/**
 * The strip's render model, pure for tests: chips in priority order with
 * first-run detection and exactly one promoted action (never two emphasis
 * voices). Demotion applies to every non-promoted amber while a promotion
 * is live.
 */
export function buildStripModel(capabilities: ClientCapabilities): StripModel {
  const chips: StripChip[] = includedProducts(capabilities.productPackage).map((product) => ({
    product,
    object: COPY.cards.objectNames[product],
    state: capabilities[product].state,
    verified: product === 'wordpress' ? capabilities.wordpress.verified : undefined,
    href: SECTION_HREF[product],
    promoted: false,
    demoted: false,
  }));

  const connectedCount = chips.filter((c) => c.state === 'connected').length;
  const firstRun = connectedCount === 0 && chips.some((c) => c.state === 'not_set_up');

  // Exactly one promotion: first-run → the first not-set-up step;
  // post-first-run → the first action_needed. Chips are in priority order.
  const promotedProduct = firstRun
    ? (chips.find((c) => c.state === 'not_set_up')?.product ?? null)
    : (chips.find((c) => c.state === 'action_needed')?.product ?? null);

  for (const chip of chips) {
    chip.promoted = chip.product === promotedProduct;
    chip.demoted = promotedProduct !== null && !chip.promoted && chip.state === 'action_needed';
  }

  return { chips, firstRun };
}

const PROMOTED_CLASS =
  'inline-flex items-center gap-1.5 rounded-full bg-coral px-2.5 py-0.5 text-xs font-medium text-primary-foreground hover:bg-coral-hover';

export function SetupStatusStrip() {
  const tenantId = useTenantStore((s) => s.currentTenant);
  const { capabilities, isLoading, isError, dataUpdatedAt } = useClientCapabilities(tenantId);

  if (!tenantId) return null;

  // Initial unverified load: stable-height placeholder — no spinner, no
  // wrong claims, no layout shift when the chips land.
  if (!capabilities) {
    return isLoading ? <div aria-hidden="true" className="mb-4 h-7" /> : null;
  }

  const model = buildStripModel(capabilities);
  const staleSuffix = staleSuffixFor({ isError, dataUpdatedAt });

  return (
    <div data-testid="setup-status-strip" className="mb-4 flex flex-wrap items-center gap-2">
      {model.chips.map((chip) => {
        const tooltip = COPY.stateTooltips[chip.state];
        if (chip.promoted) {
          const text = model.firstRun
            ? `${COPY.startHere} · ${chip.object}`
            : `${STATE_LABELS[chip.state]} · ${chip.object}`;
          return (
            <Link key={chip.product} to={chip.href} title={tooltip} className={PROMOTED_CLASS}>
              {text}
            </Link>
          );
        }
        const pill = (
          <StatusPill
            state={chip.state}
            object={chip.object}
            verified={chip.verified}
            staleSuffix={staleSuffix}
            className={cn(chip.demoted && 'opacity-60')}
          />
        );
        // Amber chips deep-link to the owning section; the rest are inert.
        return chip.state === 'action_needed' ? (
          <Link key={chip.product} to={chip.href} title={tooltip} className="inline-flex">
            {pill}
          </Link>
        ) : (
          <span key={chip.product} title={tooltip} className="inline-flex">
            {pill}
          </span>
        );
      })}
    </div>
  );
}
